import { useCallback, useEffect, useRef } from 'react'

import type { AttendanceStatus, RosterEntry } from '@shared/types/api'
import { ATTENDANCE_STATUS_META, statusColor, statusLabel } from '@shared/utils/attendance'

interface RosterListProps {
  roster: RosterEntry[]
  /** Тап по строке: переключает был / не был / снято */
  onToggle: (entry: RosterEntry) => void
  /** Долгое нажатие: остальные статусы и заметка */
  onOpenDetails: (entry: RosterEntry) => void
  /** Кого сейчас сохраняем — строка блокируется, чтобы не отправить два раза */
  pendingAthleteIds?: Set<string>
  disabled?: boolean
}

const LONG_PRESS_MS = 500

/**
 * Список переклички.
 *
 * Строки крупные и не пересортировываются по статусу: тренер тыкает пальцем,
 * не глядя, и список не должен прыгать под рукой.
 */
export function RosterList({
  roster,
  onToggle,
  onOpenDetails,
  pendingAthleteIds,
  disabled = false,
}: RosterListProps) {
  if (roster.length === 0) {
    return (
      <div className="py-10 text-center text-[15px]" style={{ color: 'var(--text-hint)' }}>
        В группе пока нет спортсменов
      </div>
    )
  }

  return (
    <ul className="flex flex-col" data-testid="roster-list">
      {roster.map((entry) => (
        <RosterRow
          key={entry.athlete_id}
          entry={entry}
          onToggle={onToggle}
          onOpenDetails={onOpenDetails}
          pending={pendingAthleteIds?.has(entry.athlete_id) ?? false}
          disabled={disabled}
        />
      ))}
    </ul>
  )
}

interface RosterRowProps {
  entry: RosterEntry
  onToggle: (entry: RosterEntry) => void
  onOpenDetails: (entry: RosterEntry) => void
  pending: boolean
  disabled: boolean
}

function RosterRow({ entry, onToggle, onOpenDetails, pending, disabled }: RosterRowProps) {
  // Состояние долгого нажатия живёт в ref, а не в переменных рендера:
  // между нажатием и отпусканием строка может перерисоваться (например,
  // соседняя отметка обновила доску), и локальные переменные потерялись бы.
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFired = useRef(false)

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  // Уходя с экрана, таймер за собой убираем.
  useEffect(() => cancelLongPress, [cancelLongPress])

  const startLongPress = () => {
    if (disabled) return
    longPressFired.current = false
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true
      onOpenDetails(entry)
    }, LONG_PRESS_MS)
  }

  const handleClick = () => {
    // После долгого нажатия обычный тап не должен ещё раз менять статус.
    if (longPressFired.current || disabled) return
    onToggle(entry)
  }

  const meta = entry.status ? ATTENDANCE_STATUS_META[entry.status] : undefined

  return (
    <li>
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 text-left"
        style={{
          minHeight: 56,
          borderBottom: '1px solid var(--separator)',
          opacity: pending ? 0.5 : 1,
          backgroundColor: 'transparent',
        }}
        aria-label={`${entry.name}${meta ? `, ${meta.label}` : ', без отметки'}`}
        aria-pressed={entry.marked}
        disabled={disabled || pending}
        onClick={handleClick}
        onPointerDown={startLongPress}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onContextMenu={(e) => {
          // Правая кнопка и долгий тап на десктопе открывают то же меню.
          e.preventDefault()
          if (!disabled) onOpenDetails(entry)
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: entry.marked ? statusColor(entry.status) : 'transparent',
            border: entry.marked ? 'none' : '2px solid var(--separator)',
          }}
        />

        <span className="flex-1 text-[17px]" style={{ color: 'var(--text-primary)' }}>
          {entry.name}
        </span>

        {entry.marked && (
          <span className="text-[15px]" style={{ color: 'var(--text-secondary)' }}>
            {statusLabel(entry.status)}
          </span>
        )}

        {entry.notes && (
          <span className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
            {entry.notes}
          </span>
        )}
      </button>
    </li>
  )
}

/** Кнопки статусов для нижней шторки долгого нажатия. */
export function StatusPicker({
  current,
  statuses,
  onPick,
}: {
  current?: AttendanceStatus
  statuses: AttendanceStatus[]
  onPick: (status: AttendanceStatus) => void
}) {
  return (
    <div className="flex flex-col">
      {statuses.map((status) => (
        <button
          key={status}
          type="button"
          className="flex items-center gap-3 px-4 py-3 text-left text-[17px]"
          style={{
            color: 'var(--text-primary)',
            fontWeight: status === current ? 600 : 400,
          }}
          onClick={() => onPick(status)}
        >
          <span
            aria-hidden="true"
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: ATTENDANCE_STATUS_META[status].color,
            }}
          />
          {ATTENDANCE_STATUS_META[status].label}
        </button>
      ))}
    </div>
  )
}
